import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import {
  clearFeatureFlagTenantOverride,
  describeError,
  getFeatureFlag,
  getFeatureFlagCatalog,
  updateFeatureFlag,
  type FeatureFlagDetailDto,
  type FeatureFlagTenantOverrideDto,
} from '@/lib/api';
import { describeFeatureFlagError } from '@/lib/feature-flag-errors';
import { FeatureFlagTenantOverrideModal } from '@/pages/FeatureFlagTenantOverrideModal';
import { CardTable, THead, TBody, Tr, Th, Td } from '@/components/CardTable';
import { KillSwitchBadge } from '@/components/KillSwitchBadge';

export function FeatureFlagDetailPage() {
  const { key } = useParams<{ key: string }>();
  const [data, setData] = useState<FeatureFlagDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  const [editingDisplayName, setEditingDisplayName] = useState<string | null>(null);
  const [editingDescription, setEditingDescription] = useState<string | null>(null);
  const [editingRollout, setEditingRollout] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // Confirmação dentro da página para remover o percentual legado de um kill switch com o default
  // ligado: nesse caso o PATCH muda a exposição (de N% para todas as lojas, ou para nenhuma).
  const [confirmingLegacyRemoval, setConfirmingLegacyRemoval] = useState(false);
  // PATCH em andamento: os controles que chamam saveField travam até a resposta. O ref barra o
  // segundo clique que chega antes de o estado re-renderizar a tela.
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  const [overrideTarget, setOverrideTarget] = useState<FeatureFlagTenantOverrideDto | null>(null);
  const [overrideOpen, setOverrideOpen] = useState(false);

  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    // Padrao de busca de dados do backoffice: sinalizar carregamento e limpar
    // o erro anterior antes da chamada. Sair dele exige uma camada de dados
    // (react-query ou suspense), nao um ajuste local — enquanto nao houver,
    // o que a regra pede seria pior: a tela ficaria mostrando o erro da
    // consulta passada durante a nova.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true);
    setError(null);
    getFeatureFlag(key)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((err) => !cancelled && setError(describeError(err)))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [key, reloadTick]);

  // Kill switch vem do catálogo, pela key. Sem catálogo, a tela trata a flag como comum e
  // a API continua recusando percentual, com o motivo traduzido por describeFeatureFlagError.
  const [isKillSwitch, setIsKillSwitch] = useState(false);
  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    getFeatureFlagCatalog()
      .then((items) => {
        if (!cancelled) setIsKillSwitch(items.some((item) => item.key === key && item.isKillSwitch));
      })
      .catch(() => { if (!cancelled) setIsKillSwitch(false); });
    return () => { cancelled = true; };
  }, [key]);

  if (loading) return <div>Carregando…</div>;
  if (error) return <div className="error-banner">{error}</div>;
  if (!data) return <div>Flag não encontrada.</div>;

  const { flag, tenantOverrides } = data;
  const isArchived = flag.archivedAt !== null;
  const supportsOverrides = flag.scope === 'PerTenant';
  // Percentual gravado antes da regra do kill switch (ou por SQL): o PATCH com 100 o normaliza.
  const hasLegacyPercentage = isKillSwitch
    && flag.defaultRolloutPercentage !== null
    && flag.defaultRolloutPercentage !== 100;

  async function saveField(field: 'displayName' | 'description' | 'defaultEnabled' | 'defaultRolloutPercentage', value: unknown) {
    await savePatch({ [field]: value });
  }

  async function savePatch(patch: Parameters<typeof updateFeatureFlag>[1]) {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setActionError(null);
    try {
      await updateFeatureFlag(flag.key, patch);
      setReloadTick((t) => t + 1);
      setEditingDisplayName(null);
      setEditingDescription(null);
      setEditingRollout(null);
      setConfirmingLegacyRemoval(false);
    } catch (err) {
      setActionError(describeFeatureFlagError(err));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }

  // Com o default desligado, o kill switch avalia false com qualquer percentual: trocar o legado
  // por 100 não muda nada. Com o default ligado, muda (o avaliador lê 100 como todo mundo), e a
  // tela pergunta antes se o recurso fica ligado ou desligado para todas as lojas.
  function handleRemoveLegacyPercentage() {
    if (flag.defaultEnabled) {
      setConfirmingLegacyRemoval(true);
      return;
    }
    void saveField('defaultRolloutPercentage', 100);
  }

  async function toggleDefaultEnabled(e: FormEvent<HTMLInputElement>) {
    await saveField('defaultEnabled', e.currentTarget.checked);
  }

  async function handleClearOverride(tenantId: string) {
    if (!confirm(`Remover override do tenant ${tenantId}?`)) return;
    try {
      await clearFeatureFlagTenantOverride(flag.key, tenantId);
      setReloadTick((t) => t + 1);
    } catch (err) {
      setActionError(describeFeatureFlagError(err));
    }
  }

  return (
    <div className="page" style={{ maxWidth: 960 }}>
      <Link to="/feature-flags" className="backlink">← Feature flags</Link>
      <div>
        <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 24, fontWeight: 800, margin: 0, letterSpacing: '-0.02em' }}>
          {flag.displayName}
        </h2>
        <div className="muted mono" style={{ fontSize: 13, marginTop: 6 }}>
          <span>{flag.key}</span>
          {isKillSwitch && <KillSwitchBadge />}
        </div>
      </div>

      {isArchived && (
        <div className="info-banner" style={{ marginBottom: 16 }}>
          ⚠ Flag arquivada em {new Date(flag.archivedAt!).toLocaleString('pt-BR')}. Avaliação retorna sempre false.
        </div>
      )}

      {actionError && <div className="error-banner">{actionError}</div>}

      <div className="card" style={{ marginBottom: 16 }}>
        <Row label="Escopo" value={<span className="badge">{flag.scope}</span>} />
        <Row
          label="Nome"
          value={
            editingDisplayName !== null ? (
              <span className="btn-row">
                <input value={editingDisplayName} onChange={(e) => setEditingDisplayName(e.target.value)} maxLength={200} />
                <button className="btn btn-secondary" onClick={() => saveField('displayName', editingDisplayName)} disabled={saving}>Salvar</button>
                <button className="btn btn-secondary" onClick={() => setEditingDisplayName(null)}>Cancelar</button>
              </span>
            ) : (
              <span>
                {flag.displayName}
                {!isArchived && (
                  <button type="button" onClick={() => setEditingDisplayName(flag.displayName)} style={{ marginLeft: 8, background: 'none', border: 'none', color: 'var(--primary, #F97316)', cursor: 'pointer' }}>editar</button>
                )}
              </span>
            )
          }
        />
        <Row
          label="Descrição"
          value={
            editingDescription !== null ? (
              <span style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <textarea value={editingDescription} onChange={(e) => setEditingDescription(e.target.value)} rows={2} maxLength={2000} style={{ width: 320 }} />
                <button className="btn btn-secondary" onClick={() => saveField('description', editingDescription)} disabled={saving}>Salvar</button>
                <button className="btn btn-secondary" onClick={() => setEditingDescription(null)}>Cancelar</button>
              </span>
            ) : (
              <span>
                {flag.description || <span className="muted">—</span>}
                {!isArchived && (
                  <button type="button" onClick={() => setEditingDescription(flag.description ?? '')} style={{ marginLeft: 8, background: 'none', border: 'none', color: 'var(--primary, #F97316)', cursor: 'pointer' }}>editar</button>
                )}
              </span>
            )
          }
        />
        <Row
          label="Default enabled"
          value={<input type="checkbox" checked={flag.defaultEnabled} disabled={isArchived || saving} onChange={toggleDefaultEnabled} />}
        />
        <Row
          label="Default rollout %"
          value={
            editingRollout !== null ? (
              <span className="btn-row">
                <input type="text" value={editingRollout} onChange={(e) => setEditingRollout(e.target.value)} placeholder="vazio = sem rollout" style={{ width: 120 }} />
                <button className="btn btn-secondary" onClick={() => saveField('defaultRolloutPercentage', editingRollout === '' ? null : Number(editingRollout))} disabled={saving}>Salvar</button>
                <button className="btn btn-secondary" onClick={() => setEditingRollout(null)}>Cancelar</button>
              </span>
            ) : (
              <span>
                {flag.defaultRolloutPercentage ?? <span className="muted">—</span>}
                {flag.defaultRolloutPercentage !== null && '%'}
                {!isArchived && !isKillSwitch && (
                  <button type="button" onClick={() => setEditingRollout(flag.defaultRolloutPercentage?.toString() ?? '')} style={{ marginLeft: 8, background: 'none', border: 'none', color: 'var(--primary, #F97316)', cursor: 'pointer' }}>editar</button>
                )}
                {!isArchived && isKillSwitch && (
                  <span className="muted" style={{ marginLeft: 8, fontSize: 12 }}>kill switch: sem percentual</span>
                )}
                {!isArchived && hasLegacyPercentage && !confirmingLegacyRemoval && (
                  <button type="button" className="btn btn-secondary" onClick={handleRemoveLegacyPercentage} disabled={saving} style={{ marginLeft: 8 }}>
                    {flag.defaultEnabled ? 'Remover percentual…' : 'Remover percentual (100%)'}
                  </button>
                )}
                {!isArchived && hasLegacyPercentage && !flag.defaultEnabled && (
                  <span className="muted" style={{ marginLeft: 8, fontSize: 12 }}>sem efeito: o default está desligado</span>
                )}
                {!isArchived && hasLegacyPercentage && confirmingLegacyRemoval && (
                  <span role="group" aria-label="Remover percentual legado" className="info-banner" style={{ display: 'block', marginTop: 8 }}>
                    <span style={{ display: 'block', marginBottom: 8 }}>
                      Hoje o default está ligado com {flag.defaultRolloutPercentage}%, então o recurso vale
                      para {flag.defaultRolloutPercentage === 0 ? 'nenhuma loja' : `cerca de ${flag.defaultRolloutPercentage}% das avaliações`}.
                      Sem percentual, o kill switch só liga ou desliga para todas as lojas, na hora. Escolha o estado:
                    </span>
                    <span className="btn-row">
                      <button type="button" className="btn btn-secondary" onClick={() => savePatch({ defaultRolloutPercentage: 100 })} disabled={saving}>
                        Ligar para todas as lojas
                      </button>
                      <button type="button" className="btn btn-secondary" onClick={() => savePatch({ defaultEnabled: false, defaultRolloutPercentage: 100 })} disabled={saving}>
                        Desligar para todas as lojas
                      </button>
                      <button type="button" className="btn btn-secondary" onClick={() => setConfirmingLegacyRemoval(false)} disabled={saving}>
                        Cancelar
                      </button>
                    </span>
                  </span>
                )}
              </span>
            )
          }
        />
        <Row label="Criada em" value={new Date(flag.createdAt).toLocaleString('pt-BR')} />
        <Row label="Atualizada em" value={new Date(flag.updatedAt).toLocaleString('pt-BR')} />
      </div>

      {supportsOverrides && !isArchived && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h2 style={{ fontSize: 16, margin: 0 }}>Overrides por tenant</h2>
            <button type="button" className="btn" onClick={() => { setOverrideTarget(null); setOverrideOpen(true); }}
              style={{ background: 'var(--primary, #F97316)', color: '#fff', padding: '6px 12px', borderRadius: 6 }}>
              + Adicionar override
            </button>
          </div>
          {tenantOverrides.length === 0 ? (
            <p className="muted">Nenhum override. A flag usa o default para todos os tenants.</p>
          ) : (
            <div className="table-scroll">
              <CardTable>
                <THead>
                  <Tr><Th>Tenant</Th><Th>Enabled</Th><Th>Rollout</Th><Th>Atualizado</Th><Th></Th></Tr>
                </THead>
                <TBody>
                  {tenantOverrides.map((o) => (
                    <Tr key={o.tenantId}>
                      <Td heading style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{o.tenantId}</Td>
                      <Td label="Enabled">{o.enabled ? 'on' : 'off'}</Td>
                      <Td label="Rollout">{o.rolloutPercentage ?? '—'}{o.rolloutPercentage !== null ? '%' : ''}</Td>
                      <Td label="Atualizado">{new Date(o.updatedAt).toLocaleString('pt-BR')}</Td>
                      <Td action>
                        <button type="button" onClick={() => { setOverrideTarget(o); setOverrideOpen(true); }} style={{ background: 'none', border: 'none', color: 'var(--primary, #F97316)', cursor: 'pointer' }}>editar</button>
                        <button type="button" onClick={() => handleClearOverride(o.tenantId)} style={{ marginLeft: 12, background: 'none', border: 'none', color: '#DC2626', cursor: 'pointer' }}>remover</button>
                      </Td>
                    </Tr>
                  ))}
                </TBody>
              </CardTable>
            </div>
          )}
        </div>
      )}

      {overrideOpen && (
        <FeatureFlagTenantOverrideModal
          flagKey={flag.key}
          existing={overrideTarget}
          onClose={() => setOverrideOpen(false)}
          onSaved={() => {
            setOverrideOpen(false);
            setOverrideTarget(null);
            setReloadTick((t) => t + 1);
          }}
        />
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="detail-grid" style={{ padding: '6px 0', borderBottom: '1px solid var(--color-border)' }}>
      <div className="muted">{label}</div>
      <div>{value}</div>
    </div>
  );
}
